/**
 * This file is used to set the scale of the brand logos on the case study cards as they have different heights on different devices.
 * A scale by brand, by context and by device — only on case study cards. 
 * 1 = base height from CSS (--logo-h) in this site
 * 1.2 = 20% bigger · 0.85 = 15% smaller
 *
 * Devices: desktop · tablet (iPad) · mobile
 * If tablet is omitted, it defaults to 65% of desktop.
 *
 * Contexts:
 *   homepageHeroPmpLogos        PMP row in mobile hero (with Nexxen)
 *   homepageCaseStudyCards      cards Case Studies on home, landings and Explore More
 *   caseStudyExploreMoreCards   alias of homepageCaseStudyCards
 *   caseStudiesListPage         list /case-studies
 *
 * If the brand doesn't have the site, use `default`.
 * If there's no entry, the scale is 1.
 */

export const LOGO_PLACEMENT = {
  homepageHeroPmpLogos: "homepageHeroPmpLogos",
  homepageCaseStudyCards: "homepageCaseStudyCards",
  caseStudyExploreMoreCards: "caseStudyExploreMoreCards",
  caseStudiesListPage: "caseStudiesListPage",
}

export const BRAND_LOGO_SCALES = {
  "index-exchange": {
    homepageHeroPmpLogos: { desktop: 1, mobile: 1.15 },
  },
  openx: {
    default: { desktop: 0.9, mobile: 0.9 },
    homepageHeroPmpLogos: { desktop: 1, mobile: 1 },
    homepageCaseStudyCards: { desktop: 0.7, mobile: 0.6 },
    caseStudiesListPage: { desktop: 0.7, mobile: 0.6 },
    homepageHeroPmpLogos: { desktop: 1, mobile: 0.9 },
  },
  magnite: {
    homepageHeroPmpLogos: { desktop: 1, mobile: 1.2 },
  },
  pubmatic: {
    homepageHeroPmpLogos: { desktop: 1, mobile: 1 },
  },
  nexxen: {
    homepageHeroPmpLogos: { desktop: 1, mobile: 0.8 },
  },
  equativ: {
    homepageHeroPmpLogos: { desktop: 1, mobile: 1 },
  },
  medianet: {
    homepageHeroPmpLogos: { desktop: 1, mobile: 1 },
  },
  inpowered: {
    default: { desktop: 1, mobile: 1 },
    homepageCaseStudyCards: { desktop: 0.9, mobile: 0.9 },
    caseStudiesListPage: { desktop: 0.9, mobile: 0.8 },
  },
  precisely: {
    caseStudiesListPage: { desktop: 0.7, mobile: 0.7 },
    homepageCaseStudyCards: { desktop: 0.9, mobile: 0.7 },
  },
}

const LOGO_ALIASES = {
  "in-powered": "inpowered",
  inpwrd: "inpowered",
}

const pair = (desktop, mobile = desktop, tablet) => ({
  desktop,
  tablet: tablet ?? roundScale(desktop * 0.65),
  mobile,
})

const roundScale = value => Math.round(value * 100) / 100

const toPair = (value, fallback = pair(1)) => {
  if (value == null) return fallback
  if (typeof value === "number") return pair(value)
  const desktop = value.desktop ?? value.mobile ?? fallback.desktop
  const mobile = value.mobile ?? value.desktop ?? fallback.mobile
  const tablet =
    value.tablet ?? roundScale(desktop * 0.65)
  return pair(desktop, mobile, tablet)
}

export const logoBrandFromSrc = (src, brand) => {
  if (brand) return brand
  const filename = String(src && src.default ? src.default : src)
    .toLowerCase()
    .split(/[/?#]/)
    .filter(Boolean)
    .pop()
  if (!filename) return ""

  const alias = Object.keys(LOGO_ALIASES).find(k => filename.includes(k))
  if (alias) return LOGO_ALIASES[alias]

  const known = Object.keys(BRAND_LOGO_SCALES).find(k => filename.includes(k))
  return known || filename.replace(/\.[a-z0-9]+$/, "")
}

export const getBrandLogoScalePair = (src, context, brand) => {
  const resolvedContext =
    context === LOGO_PLACEMENT.caseStudyExploreMoreCards
      ? LOGO_PLACEMENT.homepageCaseStudyCards
      : context
  const key = logoBrandFromSrc(src, brand)
  const entry = key && BRAND_LOGO_SCALES[key]
  if (!entry) return pair(1)
  if (typeof entry === "number") return toPair(entry)
  return toPair(entry[resolvedContext] ?? entry.default)
}

export const getBrandLogoScaleStyle = (src, context, brand) => {
  const { desktop, tablet, mobile } = getBrandLogoScalePair(src, context, brand)
  return {
    "--logo-scale-desktop": String(desktop),
    "--logo-scale-tablet": String(tablet),
    "--logo-scale-mobile": String(mobile),
  }
}
